import { Wallet as WalletType } from '@shared/constants';
import { useProviders } from '@shared/hooks';

export function useShouldUseDeeplink() {
  const { getEthereumProvider, getFirstProviderWalletType } = useProviders();

  const isMobile = (): boolean => {
    // 1. Проверка User Agent
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileUserAgentRegex =
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|rim)|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
    const tabletUserAgentRegex = /android|ipad|playbook|silk/i;

    if (mobileUserAgentRegex.test(userAgent) || tabletUserAgentRegex.test(userAgent)) {
      return true;
    }

    // 2. Проверка наличия 'window.orientation' (часто указывает на мобильные устройства)
    if (typeof window.orientation !== 'undefined') {
      return true;
    }

    // 3. Проверка Touch-событий (современные мобильные устройства обычно имеют сенсорный ввод)
    // 'ontouchstart' для старых браузеров и Safari
    // 'maxTouchPoints' для более современных браузеров
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      // Дополнительная проверка, чтобы исключить десктопы с тачскринами,
      // если это критично. Однако большинство таких "десктопов"
      // все равно ведут себя как мобильные для большинства целей.
      return true;
    }

    // 4. Проверка с помощью медиа-запросов (наиболее надежный для определения "мобильного" контекста)
    // 'any-pointer: coarse' указывает на наличие "грубого" указателя (палец)
    // 'any-hover: none' указывает на отсутствие возможности наведения (т.е. нет мыши)
    // 'max-width: 768px' - это просто пример, вы можете настроить его под свои нужды
    // Важно: matchMedia не поддерживается в IE. Если нужна поддержка IE, используйте полифилл или более старые методы.
    if (
      window.matchMedia &&
      (window.matchMedia('(max-width: 768px)').matches || // Общий размер мобильного/планшетного экрана
        window.matchMedia('(pointer: coarse)').matches ||
        window.matchMedia('(any-pointer: coarse)').matches ||
        window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(any-hover: none)').matches)
    ) {
      return true;
    }

    // Если ни одно из условий не сработало, считаем устройство немобильным
    return false;
  };

  const shouldUseDeeplink = (wallet: WalletType): boolean => {
    const provider = getEthereumProvider(wallet);

    if (!!provider) {
      return false;
    }

    return isMobile();
  };

  const shouldSkipConnectionModal = (): boolean => {
    return isMobile() && !shouldUseDeeplink(getFirstProviderWalletType());
  };

  return { shouldUseDeeplink, shouldSkipConnectionModal, isMobile };
}
